import { Controller } from "@hotwired/stimulus"
import { isMobileApp } from "../packs/global/utils"
import { tomSelectRender } from "../packs/global/tom_select_texte"
import TomSelect from "tom-select"

const KINDS = {
  DEFAULT: "default",
  PERSONEN_SUCHE: "personen-suche",
  PFERD_SUCHE: "pferd-suche",
  REITER_SUCHE: "reiter-suche",
  ORT_SUCHE: "ort-suche",
}

const ASYNCHRONE_SUCHEN = [KINDS.PERSONEN_SUCHE, KINDS.PFERD_SUCHE, KINDS.REITER_SUCHE, KINDS.ORT_SUCHE]

// Connects to data-controller="tom-select"
export default class extends Controller {
  static values = {
    create: { type: Boolean, default: false },
    hidePlaceholder: { type: Boolean, default: true },
    limit: { type: Number, default: null }, // max Anzahl Einträge die dargestellt werden (wären sonst 50)
    kind: { type: String, default: "default" },
    labelFeld: { type: String, default: "" }, // Selector eines Feldes im selben Container, welches bei Auswahl das Label erhält
    url: { type: String, default: "" }, // Optionale Such-URL, überschreibt die Standard-URL der asynchronen Suche
  }

  connect() {
    this.init()
  }

  disconnect() {
    this.destroy()
  }

  init() {
    this.destroy()

    if (!isMobileApp || ASYNCHRONE_SUCHEN.includes(this.kindValue)) {
      this.select = new TomSelect(this.element, this.fetchOptions())
    }

    if (this.element.disabled || this.element.closest("fieldset[disabled]") !== null) {
      this.select?.disable()
    }
  }

  clear() {
    this.select?.clear()
  }

  enable() {
    this.element.disabled = false
    this.select?.enable()
  }

  disable() {
    this.element.disabled = true
    this.select?.disable()
  }

  destroy() {
    this.select?.destroy()
  }

  blur() {
    this.select?.blur()
  }

  fetchOptions() {
    return {
      ...this.kindOptions(),
      render: tomSelectRender,
    }
  }

  kindOptions() {
    switch (this.kindValue) {
      case KINDS.PERSONEN_SUCHE:
        return this.personenSucheOptions
      case KINDS.PFERD_SUCHE:
        return this.pferdSucheOptions
      case KINDS.REITER_SUCHE:
        return this.reiterSucheOptions
      case KINDS.ORT_SUCHE:
        return this.ortSucheOptions
      default:
        return this.defaultOptions
    }
  }

  get defaultOptions() {
    const plugins = []

    if (this.isMultiple) {
      plugins.push("remove_button")
    }

    return {
      plugins: plugins,
      create: this.createValue,
      createOnBlur: this.createValue, // erstellt neuen Eintrag, wenn bei Blur etwas eingetippt wurde
      maxOptions: this.limitValue,
      hidePlaceholder: this.hidePlaceholderValue,
      onItemAdd: () => {
        this.select?.setTextboxValue("") // wenn Textbox nicht geleert wird, bleibt der eingegeben Wert stehen, auch wenn er als Tag hinzugefügt wurde
        if (this.isMultiple) {
          this.select?.refreshOptions() // ohne diese Option bleibt die Auswahl der Option bestehen, dh es werden nicht mehr alle Optionen gesetzt
        }
      },
      onInitialize: () => {
        this.element.dispatchEvent(new Event("tomselect:initialized"))
      },
      onChange: () => {
        if (!this.isMultiple) {
          this.select?.blur() // bei single select soll nach Auswahl direkt geblurred werden, damit der Cursor nicht mehr im Feld ist
        }
      }
    }
  }

  asynchroneSucheOptions(basisUrl, filter = {}) {
    return {
      valueField: "id",
      labelField: "label",
      searchField: "label",
      hidePlaceholder: this.hidePlaceholderValue,
      load: (query, callback) => {
        let url = basisUrl + "?term=" + encodeURIComponent(query)
        Object.entries(filter).forEach(([dataAttribut, parameter]) => {
          const wert = this.element.dataset[dataAttribut]
          if (wert) {
            url += `&${parameter}=${encodeURIComponent(wert)}`
          }
        })
        fetch(url)
          .then((response) => response.json())
          .then((json) => {
            callback(json)
          })
          .catch(() => {
            callback()
          })
      },
      onChange: (value) => {
        this.labelUebernehmen(value)
      },
    }
  }

  labelUebernehmen(value) {
    if (!this.labelFeldValue) return

    const labelFeld = this.element.parentElement.querySelector(this.labelFeldValue)
    if (labelFeld) {
      labelFeld.value = this.select?.options[value]?.label ?? ""
    }
  }

  get isMultiple() {
    return this.element.multiple
  }

  get personenSucheOptions() {
    return this.asynchroneSucheOptions(this.urlValue || "/personen/autocomplete", {
      mitSelfserviceBenutzer: "mit_selfservice_benutzer",
      brauchtFeiEintrag: "braucht_fei_eintrag",
      nurAktive: "nur_aktive",
      personenArt: "personen_art",
    })
  }

  get pferdSucheOptions() {
    return this.asynchroneSucheOptions("/pferde/autocomplete", {
      nurLebende: "nur_lebende",
      nurAktive: "nur_aktive",
    })
  }

  get reiterSucheOptions() {
    return this.asynchroneSucheOptions("/reiter/autocomplete", {
      brauchtFeiEintrag: "braucht_fei_eintrag",
    })
  }

  get ortSucheOptions() {
    return this.asynchroneSucheOptions("/plz")
  }
}
